import { useState, useCallback } from 'react';
import { TEMPLATE_FIELDS, TemplateField, FieldSection } from '../../../utils/templateFields';
import { TEMPLATE_DEFAULTS } from '../../../utils/templateContent';

interface TemplateFillModalProps {
  templateId: string;
  templateTitle: string;
  templateCredits: number;
  downloadType: 'html' | 'pdf' | 'docx';
  isProcessing: boolean;
  onDownload: (fieldData: Record<string, string>) => Promise<void>;
  onClose: () => void;
}

const formatDateValue = (value: string, type: string): string => {
  if (type === 'date' && value) {
    const d = new Date(value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return value;
};

const FieldInput = ({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string;
  onChange: (key: string, val: string) => void;
}) => {
  const inputClass =
    'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 transition-colors bg-white';

  if (field.type === 'textarea') {
    return (
      <textarea
        id={field.key}
        rows={field.rows || 3}
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
        placeholder={field.placeholder}
        className={`${inputClass} resize-none`}
      />
    );
  }

  return (
    <input
      id={field.key}
      type={field.type}
      value={value}
      onChange={(e) => onChange(field.key, e.target.value)}
      placeholder={field.placeholder}
      className={inputClass}
    />
  );
};

const TemplateFillModal = ({
  templateId,
  templateTitle,
  templateCredits,
  downloadType,
  isProcessing,
  onDownload,
  onClose,
}: TemplateFillModalProps) => {
  const sections = TEMPLATE_FIELDS[templateId] || [];
  const defaults = TEMPLATE_DEFAULTS[templateId] || {};

  const [formData, setFormData] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    sections.forEach((sec) => {
      sec.fields.forEach((f) => {
        initial[f.key] = '';
      });
    });
    return initial;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev;
    });
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    sections.forEach((sec) => {
      sec.fields.forEach((f) => {
        if (f.required && !formData[f.key]?.trim()) {
          newErrors[f.key] = 'This field is required';
        }
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0] || '';
      document.getElementById(firstErrorKey)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Merge with defaults for any blank fields, format dates
    const finalData: Record<string, string> = {};
    sections.forEach((sec) => {
      sec.fields.forEach((f) => {
        const raw = formData[f.key]?.trim() || '';
        finalData[f.key] = raw ? formatDateValue(raw, f.type) : (defaults[f.key] || '');
      });
    });

    await onDownload(finalData);
  };

  const extLabel = downloadType === 'pdf' ? 'PDF' : downloadType === 'docx' ? 'DOCX' : 'HTML';
  const extIcon =
    downloadType === 'pdf'
      ? 'ri-file-pdf-line text-red-500'
      : downloadType === 'docx'
      ? 'ri-file-word-line text-green-600'
      : 'ri-file-text-line text-gray-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white rounded-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[92vh] overflow-hidden shadow-2xl">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-gray-900 rounded-xl">
              <i className="ri-edit-2-line text-white text-base"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{templateTitle}</h2>
              <p className="text-xs text-gray-500">Fill in your details — downloaded document will use your info</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {sections.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-file-text-line text-4xl block mb-2"></i>
              <p className="text-sm">No fields to fill for this template.</p>
              <p className="text-xs mt-1">Click download to proceed.</p>
            </div>
          )}

          {sections.map((section: FieldSection) => {
            const isTwoCol = section.cols === 2;
            return (
              <div key={section.title}>
                {/* Section header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${section.icon} text-gray-700 text-sm`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
                  <div className="flex-1 h-px bg-gray-100 ml-1"></div>
                </div>

                <div className={isTwoCol ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
                  {section.fields.map((field: TemplateField) => (
                    <div key={field.key} className={isTwoCol && field.fullWidth ? 'col-span-2' : ''}>
                      <label
                        htmlFor={field.key}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        {field.label}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                        {!field.required && (
                          <span className="text-gray-400 font-normal ml-1">
                            {defaults[field.key]
                              ? `(default: ${defaults[field.key].substring(0, 28)}${defaults[field.key].length > 28 ? '…' : ''})`
                              : '(optional)'}
                          </span>
                        )}
                      </label>
                      <FieldInput field={field} value={formData[field.key] || ''} onChange={handleChange} />
                      {errors[field.key] && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i> {errors[field.key]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Bottom spacer */}
          <div className="h-2"></div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-information-line text-gray-400"></i>
            </div>
            <span>Optional fields use sample data if left blank</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isProcessing}
              className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isProcessing ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                  Generating…
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${extIcon} text-sm`}></i>
                  </div>
                  Download as {extLabel}
                  <span className="flex items-center gap-0.5 bg-white/20 rounded-full px-2 py-0.5 text-xs">
                    <i className="ri-coin-line text-amber-300 text-xs"></i>
                    {templateCredits}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateFillModal;
